import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { Plus, Search, Edit, Copy, Archive, AlertCircle, Check, X } from 'lucide-react';

const ClassTypesManager = () => {
  const [classTypes, setClassTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingClassType, setEditingClassType] = useState(null);
  const [success, setSuccess] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Load class types
  const loadClassTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const types = await api.classTypes.getAll();
      setClassTypes(types);
    } catch (err) {
      console.error('Error loading class types:', err);
      setError('Failed to load class types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClassTypes();
  }, []);

  // Filter class types
  const filteredClassTypes = classTypes.filter(classType => {
    const matchesSearch = classType.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        classType.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling
    
    try {
      setFormLoading(true);
      setFormError('');
      
      if (!formData.name.trim()) {
        setFormError('Class name is required');
        return;
      }

      let response;
      if (editingClassType) {
        response = await api.classTypes.update(editingClassType.$id, {
          name: formData.name.trim(),
          category: formData.category.trim(),
          description: formData.description.trim()
        });
      } else {
        response = await api.classTypes.create({
          name: formData.name.trim(),
          category: formData.category.trim(),
          description: formData.description.trim()
        });
      }

      if (response.success) {
        setSuccess(editingClassType ? 'Class type updated successfully!' : 'Class type created successfully!');
        setEditingClassType(null);
        setFormData({ name: '', category: '', description: '' });
        await loadClassTypes();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setFormError(response.message || 'Failed to save class type');
      }
    } catch (err) {
      console.error('Error saving class type:', err);
      setFormError('Failed to save class type: ' + (err.message || 'Unknown error'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (classType) => {
    setEditingClassType(classType);
    setFormData({
      name: classType.name,
      category: classType.category || '',
      description: classType.description || ''
    });
    setFormError('');
  };

  const handleAdd = () => {
    setEditingClassType(null);
    setFormData({ name: '', category: '', description: '' });
    setFormError('');
  };

  const handleCancel = () => {
    setEditingClassType(null);
    setFormData({ name: '', category: '', description: '' });
    setFormError('');
  };

  const handleDuplicate = (classType) => {
    setEditingClassType(null);
    setFormData({
      name: `${classType.name} (Copy)`,
      category: classType.category || '',
      description: classType.description || ''
    });
    setFormError('');
  };

  const handleToggleActive = async (classType) => {
    try {
      const response = await api.classTypes.update(classType.$id, {
        isActive: !classType.isActive
      });
      
      if (response.success) {
        setSuccess(`Class type ${classType.isActive ? 'archived' : 'activated'} successfully!`);
        await loadClassTypes();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message || 'Failed to update class type status');
      }
    } catch (err) {
      console.error('Error toggling class type status:', err);
      setError('Failed to update class type status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-purple-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-gray-400">Loading class types...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {success && (
        <div className="p-3 bg-green-900 bg-opacity-20 border border-green-700 rounded text-green-400 text-sm flex items-center">
          <Check size={16} className="mr-2" />
          {success}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-900 bg-opacity-20 border border-red-700 rounded text-red-400 text-sm flex items-center">
          <AlertCircle size={16} className="mr-2" />
          {error}
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-white">Class Types</h3>
          <p className="text-gray-400 text-sm">Manage the types of classes your business offers</p>
        </div>
        <Button
          variant="primary"
          onClick={handleAdd}
          className="bg-purple-600 hover:bg-purple-700 flex items-center"
        >
          <Plus size={16} className="mr-2" />
          Add Class Type
        </Button>
      </div>

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search class types..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
          />
        </div>
      </div>

      {/* Add/Edit Form */}
      {(editingClassType || formData.name) && (
        <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4 border border-gray-600">
          <h4 className="text-lg font-medium text-white mb-4">
            {editingClassType ? 'Edit Class Type' : 'Add New Class Type'}
          </h4>
          
          {formError && (
            <div className="mb-4 p-3 bg-red-900 bg-opacity-20 border border-red-700 rounded text-red-400 text-sm">
              {formError}
            </div>
          )}

          <form onSubmit={handleFormSubmit} className="space-y-4">
            <Input
              label="Class Name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Advanced Conversation Class"
              required
            />

            <Input
              label="Category (optional)"
              type="text"
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              placeholder="e.g., conversation, business, exam_prep"
              helperText="Used for grouping classes (comma-separated for multiple categories)"
            />

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this class type is about..."
                rows={3}
                className="w-full p-3 border border-gray-600 bg-[rgba(255,255,255,0.05)] rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancel}
                className="bg-gray-600 hover:bg-gray-500"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                isLoading={formLoading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {editingClassType ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Class Types List */}
      <div className="max-h-96 overflow-y-auto space-y-3 border border-gray-700 rounded-lg p-4">
        {filteredClassTypes.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p>No class types found</p>
            {searchTerm && (
              <Button
                variant="ghost"
                onClick={() => setSearchTerm('')}
                className="mt-2"
              >
                Clear search
              </Button>
            )}
          </div>
        ) : (
          filteredClassTypes.map((classType) => (
            <div
              key={classType.$id}
              className={`p-4 rounded-lg border transition-all ${
                classType.isActive 
                  ? 'bg-gray-700 bg-opacity-50 border-gray-600' 
                  : 'bg-gray-800 bg-opacity-30 border-gray-700 opacity-60'
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-medium text-white text-lg">{classType.name}</h4>
                    
                    {/* Status and usage badges */}
                    <div className="flex items-center gap-2">
                      {!classType.isActive && (
                        <span className="px-2 py-1 text-xs bg-gray-600 text-gray-300 rounded">
                          Archived
                        </span>
                      )}
                      <span className="text-xs text-gray-400">
                        {classType.usageCount || 0} classes
                      </span>
                    </div>
                  </div>
                  
                  {classType.category && (
                    <p className="text-sm text-purple-400 mb-1">
                      <strong>Category:</strong> {classType.category}
                    </p>
                  )}
                  
                  {classType.description && (
                    <p className="text-sm text-gray-300">
                      {classType.description}
                    </p>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 ml-4">
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => handleEdit(classType)}
                    className="text-blue-400 hover:text-blue-300"
                    title="Edit"
                  >
                    <Edit size={16} />
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => handleDuplicate(classType)}
                    className="text-green-400 hover:text-green-300"
                    title="Duplicate"
                  >
                    <Copy size={16} />
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => handleToggleActive(classType)}
                    className={classType.isActive ? "text-yellow-400 hover:text-yellow-300" : "text-green-400 hover:text-green-300"}
                    title={classType.isActive ? "Archive" : "Activate"}
                  >
                    <Archive size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ClassTypesManager;